import { useCallback, useState, type FormEvent } from 'react';
import { koreaDate } from '@chagok/domain';
import { api, ApiError, newId } from '../api/client';
import type { Occurrence, RecurringRule, Transaction } from '../api/types';
import { CategoryPicker, Modal } from '../components/Modal';
import { categoryName, childCategories, paymentMethods, topCategories, withCurrent } from '../lib/catalog';
import { krw, parseAmountInput } from '../lib/format';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../state';
import { ErrorBox } from './Dashboard';

const STATUS_LABEL: Record<Occurrence['status'], string> = { expected: '예정', needs_review: '분류 재선택 필요', confirmed: '확정', skipped: '건너뜀' };

/**
 * 반복 규칙(급여·월세·구독료)과 회차. 예정일이 되어도 거래를 자동으로 만들지 않고, 실제 금액·날짜를 확인한 뒤 확정한다.
 */
export function RecurringPage() {
  const { period, dataVersion, catalog, changed, toast, reloadCatalog } = useApp();
  const rules = useAsync(() => api.get<{ data: RecurringRule[] }>('/recurring-rules').then((r) => r.data), [dataVersion]);
  const occurrences = useAsync(() => api.get<{ data: Occurrence[] }>('/recurring-occurrences', { from: period.start, to: period.end }).then((r) => r.data), [period.start, period.end, dataVersion]);
  const [editing, setEditing] = useState<RecurringRule | 'new' | null>(null);
  const [confirming, setConfirming] = useState<Occurrence | null>(null);
  const [linking, setLinking] = useState<Occurrence | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function act(o: Occurrence, action: 'skip' | 'reopen') {
    setError(null);
    try {
      await api.post(`/recurring-occurrences/${o.id}/${action}`, {}, { ifMatch: o.revision });
      toast(action === 'skip' ? '이번 회차를 건너뛰었습니다.' : '다시 예정 상태로 돌렸습니다.');
      changed();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '처리하지 못했습니다.');
      if (e instanceof ApiError && e.status === 409) changed();
    }
  }

  async function toggleActive(rule: RecurringRule) {
    setError(null);
    try {
      await api.patch(`/recurring-rules/${rule.id}`, { active: !rule.active }, { ifMatch: rule.revision });
      toast(rule.active ? '중지했습니다. 앞으로의 미확정 회차는 지웠습니다.' : '다시 시작했습니다.');
      changed();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '처리하지 못했습니다.');
      if (e instanceof ApiError && e.status === 409) changed();
    }
  }

  const close = useCallback(() => {
    setEditing(null);
    setConfirming(null);
    setLinking(null);
  }, []);

  return (
    <section>
      {error && (
        <p className="notice warn" role="alert">
          {error}
        </p>
      )}
      <article className="card">
        <div className="card-head">
          <div>
            <h2>이 기간의 예정 항목</h2>
            <p className="muted">예정 금액은 실제 합계에 넣지 않습니다. 확정하면 실제 금액으로 거래가 기록됩니다.</p>
          </div>
        </div>
        {occurrences.error && !occurrences.data ? (
          <ErrorBox message={occurrences.error.message} retry={occurrences.reload} />
        ) : occurrences.data?.length ? (
          occurrences.data.map((o) => (
            <div className={`transaction status-${o.status}`} key={o.id}>
              <span className="icon">↻</span>
              <div className="details">
                <b>{o.ruleName}</b>
                <small>
                  {o.scheduledDate} · {o.kind === 'income' ? '수입' : '지출'} · <span className={`chip ${o.status}`}>{STATUS_LABEL[o.status]}</span>
                  {o.status === 'confirmed' && o.transactionAmountKrw ? ` · 실제 ${krw(o.transactionAmountKrw)} (${o.transactionDate})` : ''}
                </small>
              </div>
              <span className="price">{o.expectedAmountKrw ? krw(o.expectedAmountKrw) : '금액 미정'}</span>
              <span className="row-actions">
                {(o.status === 'expected' || o.status === 'needs_review') && (
                  <>
                    <button className="delete" onClick={() => setConfirming(o)}>
                      확정
                    </button>
                    <button className="delete" onClick={() => setLinking(o)}>
                      기존 기록 연결
                    </button>
                    <button className="delete" onClick={() => void act(o, 'skip')}>
                      건너뛰기
                    </button>
                  </>
                )}
                {o.status === 'skipped' && (
                  <button className="delete" onClick={() => void act(o, 'reopen')}>
                    다시 열기
                  </button>
                )}
              </span>
            </div>
          ))
        ) : (
          <div className="empty">이 기간에 예정 항목이 없습니다. 회차는 오늘부터 30일 앞까지 만들어집니다.</div>
        )}
      </article>

      <article className="card spaced">
        <div className="card-head">
          <div>
            <h2>반복 규칙</h2>
            <p className="muted">급여·월세·구독료처럼 반복되는 수입·지출입니다. 날짜가 없는 달은 말일로 잡습니다.</p>
          </div>
          <button className="primary" onClick={() => setEditing('new')}>
            ＋ 반복 항목
          </button>
        </div>
        {rules.data?.length ? (
          rules.data.map((r) => (
            <div className={`transaction ${r.active ? '' : 'catalog-archived'}`} key={r.id}>
              <span className="icon">{r.kind === 'income' ? '＋' : '↻'}</span>
              <div className="details">
                <b>
                  {r.name}
                  {r.active ? '' : ' · 중지됨'}
                </b>
                <small>
                  {r.intervalMonths === 1 ? '매월' : `${r.intervalMonths}개월마다`} {r.dayOfMonth}일 · {r.categoryId ? `${categoryName(catalog, r.categoryId)} › ${categoryName(catalog, r.subcategoryId)}` : '분류 없음'}
                  {r.needsReview ? ' · 분류 재선택 필요' : ''}
                  {r.nextRunDate ? ` · 다음 생성 ${r.nextRunDate}` : ''}
                  {r.amountKrw && r.intervalMonths > 1 ? ` · 월 환산 ${krw(r.monthlyEquivalentKrw)}` : ''}
                </small>
              </div>
              <span className="price">{r.amountKrw ? krw(r.amountKrw) : '변동 금액'}</span>
              <span className="row-actions">
                <button className="delete" onClick={() => setEditing(r)}>
                  수정
                </button>
                <button className="delete" onClick={() => void toggleActive(r)}>
                  {r.active ? '중지' : '재개'}
                </button>
              </span>
            </div>
          ))
        ) : rules.loading ? (
          <div className="empty">불러오는 중…</div>
        ) : (
          <div className="empty">등록된 반복 항목이 없습니다.</div>
        )}
      </article>

      <Modal open={editing !== null} onClose={close} title={editing === 'new' ? '반복 항목 추가' : '반복 항목 수정'}>
        {editing !== null && (
          <RuleForm
            rule={editing === 'new' ? null : editing}
            onDone={(message) => {
              close();
              toast(message);
              changed();
            }}
            onArchived={() => void reloadCatalog()}
          />
        )}
      </Modal>
      <Modal open={confirming !== null} onClose={close} title="예정 항목 확정">
        {confirming && (
          <ConfirmForm
            occurrence={confirming}
            rule={rules.data?.find((r) => r.id === confirming.ruleId) ?? null}
            onDone={() => {
              close();
              toast('확정했습니다. 실제 거래로 기록했어요.');
              changed();
            }}
          />
        )}
      </Modal>
      <Modal open={linking !== null} onClose={close} title="기존 기록과 연결">
        {linking && (
          <LinkForm
            occurrence={linking}
            onDone={() => {
              close();
              toast('연결했습니다.');
              changed();
            }}
          />
        )}
      </Modal>
    </section>
  );
}

function RuleForm({ rule, onDone, onArchived }: { rule: RecurringRule | null; onDone: (message: string) => void; onArchived: () => void }) {
  const { catalog } = useApp();
  const [kind, setKind] = useState<'income' | 'expense'>(rule?.kind ?? 'expense');
  const [name, setName] = useState(rule?.name ?? '');
  const [variable, setVariable] = useState(rule ? rule.amountKrw === null : false);
  const [amount, setAmount] = useState(rule?.amountKrw ?? '');
  const [categoryId, setCategoryId] = useState(rule?.categoryId ?? '');
  const [subcategoryId, setSubcategoryId] = useState(rule?.subcategoryId ?? '');
  const [paymentMethodId, setPaymentMethodId] = useState(rule?.paymentMethodId ?? '');
  const [intervalMonths, setIntervalMonths] = useState(rule?.intervalMonths ?? 1);
  const [dayOfMonth, setDayOfMonth] = useState(rule?.dayOfMonth ?? Number(koreaDate().slice(8)));
  const [startDate, setStartDate] = useState(rule?.startDate ?? koreaDate());
  const [endDate, setEndDate] = useState(rule?.endDate ?? '');
  const [error, setError] = useState<string | null>(null);
  const [key] = useState(newId);
  const [busy, setBusy] = useState(false);

  const tops = withCurrent(topCategories(catalog, kind), topCategories(catalog, kind, true), rule?.categoryId);
  const subs = withCurrent(childCategories(catalog, categoryId), childCategories(catalog, categoryId, true), rule?.subcategoryId);
  const payments = withCurrent(paymentMethods(catalog, kind), paymentMethods(catalog, kind, true), rule?.paymentMethodId);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const amountKrw = variable ? null : parseAmountInput(amount);
    if (!variable && !amountKrw) return setError('금액은 1원 이상의 정수로 입력하거나 ‘변동 금액’을 선택해주세요.');
    const body = { name, amountKrw, categoryId, subcategoryId, paymentMethodId: paymentMethodId || null, intervalMonths, dayOfMonth, startDate, endDate: endDate || null };
    setBusy(true);
    setError(null);
    try {
      if (rule) await api.patch(`/recurring-rules/${rule.id}`, body, { ifMatch: rule.revision });
      else await api.post('/recurring-rules', { kind, ...body }, { idempotencyKey: key });
      onDone(rule ? '수정했습니다. 앞으로의 미확정 회차에만 반영됩니다.' : '반복 항목을 등록했어요.');
    } catch (e) {
      setError(e instanceof ApiError ? (e.code === 'REVISION_CONFLICT' ? '다른 기기에서 먼저 수정했습니다. 닫고 다시 열어 최신 값을 확인해주세요.' : e.message) : '저장하지 못했습니다.');
      if (e instanceof ApiError && e.code === 'CATEGORY_ARCHIVED') onArchived();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)}>
      <div className="form-grid">
        {!rule && (
          <label className="full">
            종류
            <select
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as 'income' | 'expense');
                setCategoryId('');
                setSubcategoryId('');
                setPaymentMethodId('');
              }}
            >
              <option value="expense">지출 (월세·구독료 등)</option>
              <option value="income">수입 (급여·정기수당 등)</option>
            </select>
          </label>
        )}
        <label className="full">
          이름
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required placeholder="예: 넷플릭스" />
        </label>
        <label>
          예정 금액 (원)
          <input inputMode="numeric" value={variable ? '' : amount} onChange={(e) => setAmount(e.target.value)} disabled={variable} required={!variable} />
        </label>
        <label className="check">
          <input type="checkbox" checked={variable} onChange={(e) => setVariable(e.target.checked)} /> 변동 금액 (전기요금 등)
        </label>
        <CategoryPicker
          tops={tops}
          subs={subs}
          categoryId={categoryId}
          subcategoryId={subcategoryId}
          onChange={(c, s) => {
            setCategoryId(c);
            setSubcategoryId(s);
          }}
        />
        <label className="full">
          {kind === 'income' ? '입금수단' : '결제수단'}
          <select value={paymentMethodId} onChange={(e) => setPaymentMethodId(e.target.value)}>
            <option value="">선택 안 함</option>
            {payments.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.active ? '' : ' (삭제됨)'}
              </option>
            ))}
          </select>
        </label>
        <label>
          반복 간격
          <select value={intervalMonths} onChange={(e) => setIntervalMonths(Number(e.target.value))}>
            {[1, 2, 3, 6, 12].map((n) => (
              <option key={n} value={n}>
                {n === 1 ? '매월' : n === 12 ? '매년' : `${n}개월마다`}
              </option>
            ))}
          </select>
        </label>
        <label>
          날짜 (일)
          <input type="number" min={1} max={31} value={dayOfMonth} onChange={(e) => setDayOfMonth(Number(e.target.value))} required />
        </label>
        <label>
          시작일
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
        </label>
        <label>
          종료일 (선택)
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
      </div>
      <p className="hint">날짜가 없는 달(예: 31일 → 2월)은 말일로 잡습니다. 시작일·종료일은 포함합니다.</p>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <button className="primary full save" disabled={busy}>
        {busy ? '저장 중…' : '저장'}
      </button>
    </form>
  );
}

function ConfirmForm({ occurrence, rule, onDone }: { occurrence: Occurrence; rule: RecurringRule | null; onDone: () => void }) {
  const { catalog } = useApp();
  const needsCategory = occurrence.status === 'needs_review' || !rule?.categoryId;
  const [amount, setAmount] = useState(occurrence.expectedAmountKrw ?? '');
  const [date, setDate] = useState(occurrence.scheduledDate <= koreaDate() ? occurrence.scheduledDate : koreaDate());
  const [categoryId, setCategoryId] = useState(needsCategory ? '' : (rule?.categoryId ?? ''));
  const [subcategoryId, setSubcategoryId] = useState(needsCategory ? '' : (rule?.subcategoryId ?? ''));
  // 재전송해도 한 번만 확정되도록 거래 ID·키는 폼을 여는 동안 고정한다.
  const [ids] = useState(() => ({ transactionId: newId(), key: newId() }));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const amountKrw = parseAmountInput(amount);
    if (!amountKrw) return setError('실제 금액을 1원 이상의 정수로 입력해주세요.');
    setBusy(true);
    setError(null);
    try {
      await api.post(
        `/recurring-occurrences/${occurrence.id}/confirm`,
        { transactionId: ids.transactionId, amountKrw, effectiveDate: date, ...(needsCategory && { categoryId, subcategoryId }) },
        { idempotencyKey: ids.key }
      );
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '확정하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)}>
      <p className="muted">
        ‘{occurrence.ruleName}’ {occurrence.scheduledDate} 예정분입니다. 실제 {occurrence.kind === 'income' ? '받은' : '낸'} 금액과 날짜를 확인해주세요.
      </p>
      <div className="form-grid">
        <label>
          실제 금액 (원)
          <input inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} required />
        </label>
        <label>
          실제 날짜
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </label>
        {needsCategory ? (
          <CategoryPicker
            tops={topCategories(catalog, occurrence.kind)}
            subs={childCategories(catalog, categoryId)}
            categoryId={categoryId}
            subcategoryId={subcategoryId}
            onChange={(c, s) => {
              setCategoryId(c);
              setSubcategoryId(s);
            }}
          />
        ) : (
          <p className="hint full">
            분류: {categoryName(catalog, rule?.categoryId)} › {categoryName(catalog, rule?.subcategoryId)}
          </p>
        )}
      </div>
      {needsCategory && <p className="hint">규칙의 분류가 없거나 삭제되어 이번 확정에 쓸 분류를 골라야 합니다.</p>}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <button className="primary full save" disabled={busy}>
        {busy ? '저장 중…' : '확정하고 기록'}
      </button>
    </form>
  );
}

function LinkForm({ occurrence, onDone }: { occurrence: Occurrence; onDone: () => void }) {
  const month = occurrence.scheduledDate.slice(0, 7);
  const candidates = useAsync(
    () => api.get<{ data: Transaction[] }>('/transactions', { from: `${month}-01`, to: occurrence.scheduledDate > koreaDate() ? occurrence.scheduledDate : koreaDate(), kind: occurrence.kind, limit: 100 }).then((r) => r.data),
    [occurrence.id]
  );
  const [selected, setSelected] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      await api.post(`/recurring-occurrences/${occurrence.id}/link`, { transactionId: selected }, { ifMatch: occurrence.revision });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '연결하지 못했습니다.');
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)}>
      <p className="muted">이미 직접 기록한 거래가 있으면 새로 만들지 않고 이 예정 항목과 연결합니다.</p>
      <label>
        연결할 기록
        <select value={selected} onChange={(e) => setSelected(e.target.value)} required>
          <option value="">선택 필요</option>
          {candidates.data?.map((t) => (
            <option key={t.id} value={t.id}>
              {t.effectiveDate} · {t.merchant ?? '-'} · {krw(t.amountKrw)}
            </option>
          ))}
        </select>
      </label>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <button className="primary full save" disabled={!selected}>
        연결
      </button>
    </form>
  );
}
